import * as React from 'react';
import { cn } from '@/core/lib/utils';
import { floatingInputVariants, floatingLabelVariants } from './FloatingLabelInput';
import type { VariantProps } from 'class-variance-authority';

interface FloatingLabelTextareaProps
  extends
    Omit<React.ComponentProps<'textarea'>, 'placeholder'>,
    VariantProps<typeof floatingInputVariants> {
  label: string;
  id: string;
  hasValue?: boolean;
  invalid?: boolean;
}

export function FloatingLabelTextarea({
  className,
  variant,
  label,
  id,
  hasValue: hasValueProp = false,
  invalid = false,
  disabled,
  rows,
  ...props
}: FloatingLabelTextareaProps) {
  const [isFocused, setIsFocused] = React.useState(false);
  const [prevHasValueProp, setPrevHasValueProp] = React.useState(hasValueProp);
  const [hasInternalValue, setHasInternalValue] = React.useState(hasValueProp);

  // Sync external reset (e.g. form reset) without useEffect — React Compiler-compatible
  if (prevHasValueProp !== hasValueProp) {
    setPrevHasValueProp(hasValueProp);
    setHasInternalValue(hasValueProp);
  }

  const isFloating = isFocused || hasInternalValue;

  return (
    <div className="relative w-full">
      <textarea
        {...props}
        id={id}
        placeholder=""
        data-slot="textarea"
        disabled={disabled}
        rows={rows}
        aria-invalid={invalid}
        onFocus={(e) => {
          setIsFocused(true);
          props.onFocus?.(e);
        }}
        onBlur={(e) => {
          setIsFocused(false);
          setHasInternalValue(e.target.value !== '');
          props.onBlur?.(e);
        }}
        onChange={(e) => {
          setHasInternalValue(e.target.value !== '');
          props.onChange?.(e);
        }}
        className={cn(
          floatingInputVariants({ variant }),
          // Override fixed height → auto-growing textarea
          'h-auto min-h-16 pt-5 resize-y',
          invalid && 'outline-destructive ring-2 ring-destructive/20 focus:outline-destructive',
          className,
        )}
      />

      <label
        htmlFor={id}
        className={cn(
          floatingLabelVariants({ variant, floating: isFloating }),
          // Rest position: fixed top (not vertically centered) because textarea grows
          !isFloating && 'top-[0.625rem] translate-y-0',
        )}
      >
        {label}
      </label>
    </div>
  );
}
